import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AIChat from "@/components/AIChat";
import FloatingTimer from "@/components/FloatingTimer";
import QuickActivityDialog from "@/components/QuickActivityDialog";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, Users, ListChecks, UserPlus,
  BarChart3, Settings, LogOut, ScrollText, Trophy, Plus
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/tasks", label: "Activities", icon: ListChecks, testid: "nav-tasks" },
  { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
  { to: "/team", label: "Team", icon: UserPlus, testid: "nav-team", roles: ["super_admin", "admin"] },
  { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/reports", label: "Reports", icon: BarChart3, testid: "nav-reports", roles: ["super_admin", "admin"] },
  { to: "/audit", label: "Audit log", icon: ScrollText, testid: "nav-audit", roles: ["super_admin", "admin"] },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [quickOpen, setQuickOpen] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-white" data-testid="app-shell">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-neutral-200 flex flex-col sticky top-0 h-screen overflow-y-auto" data-testid="sidebar">
        <div className="px-6 py-5 border-b border-neutral-200">
          <img
            src="/xtrack-logo.png"
            alt="Xtrack"
            className="h-10 w-auto max-w-[180px] object-contain shrink-0"
            data-testid="brand-logo"
          />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {isAdmin && (
            <Button
              onClick={() => setQuickOpen(true)}
              className="w-full mb-2 rounded-sm bg-neutral-900 hover:bg-neutral-800 text-white justify-start"
              data-testid="sidebar-quick-activity"
            >
              <Plus size={14} className="mr-2" /> New activity
            </Button>
          )}
          {NAV.filter(n => !n.roles || n.roles.includes(user?.role)).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm rounded-sm transition-colors ${
                  isActive
                    ? "bg-neutral-900 text-white"
                    : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"
                }`
              }
            >
              <n.icon size={16} />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-neutral-200 p-4">
          <div className="flex items-center gap-3 mb-3" data-testid="sidebar-user">
            <div className="w-8 h-8 rounded-sm bg-neutral-900 text-white grid place-items-center text-xs font-semibold">
              {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-neutral-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 text-xs px-3 py-2 border border-neutral-200 rounded-sm hover:bg-neutral-50 transition-colors"
          >
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="page-enter">
          <Outlet />
        </div>
      </main>

      <AIChat />
      <FloatingTimer />
      <QuickActivityDialog open={quickOpen} onOpenChange={setQuickOpen} />
    </div>
  );
}
